/* oxlint-disable jsx-a11y/no-noninteractive-element-to-interactive-role -- ARIA 1.2 combobox: <input role="combobox"> + <li role="option"> is the spec'd pattern */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import type { PillTone } from '../../components';
import { cx } from '../../lib/util/cx';
import type { CampaignChannel, CampaignStatus, CampaignType, ChannelBucket } from './api';

// --- labels ---------------------------------------------------------------------

export const TYPE_LABEL: Record<CampaignType, string> = {
  chef_recruitment: 'Chef recruitment',
  user_acquisition: 'User acquisition',
  event: 'Event',
};

export const CHANNEL_LABEL: Record<ChannelBucket, string> = {
  qrcode: 'QR code',
  email: 'Email',
  social: 'Social',
  print: 'Print',
  sms: 'SMS',
  event: 'Event',
  referral: 'Referral',
  paid: 'Paid',
  other: 'Other',
};

export const STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  archived: 'Archived',
};

export const STATUS_TONE: Record<CampaignStatus, PillTone> = {
  draft: 'neutral',
  active: 'success',
  archived: 'neutral',
};

export const COLOR_LABEL: Record<string, string> = { primary: 'Primary (brown)', secondary: 'Secondary (blue)' };

/**
 * A connection QR code's level: what a scan's connection counts as. Same names
 * as the consumer app's audience levels (`reach.level1..3`).
 */
export const CONNECTION_LEVEL_LABEL: Record<string, string> = {
  '1': 'Friends & family',
  '2': 'Friends of friends',
  '3': 'Everyone',
};

// --- languages ---------------------------------------------------------------------

/**
 * Landing languages for links and QR codes: `VITE_CAMPAIGN_LANGS`, which mirrors
 * the consumer app's `VITE_SUPPORTED_LANG` per env. (The admin's own
 * `VITE_SUPPORTED_LANG` is its UI language — English only.) `en` adds no `l`.
 */
export const CAMPAIGN_LANGS: string[] = (import.meta.env.VITE_CAMPAIGN_LANGS ?? 'en')
  .split(',')
  .map((l: string) => l.trim())
  .filter(Boolean);

const LANG_NAMES: Record<string, string> = { en: 'English', es: 'Spanish' };

export function langLabel(lang: string): string {
  if (!lang) return 'English';
  const [base, region] = lang.split('-');
  const name = LANG_NAMES[base!.toLowerCase()] ?? lang;
  return region ? `${name} (${region.toUpperCase()})` : name;
}

/** '' for English, as the BE stores it. */
export function apiLang(lang: string): string {
  return lang.toLowerCase() === 'en' ? '' : lang;
}

export function variantLabel(variant: string, description?: string): string {
  if (!variant) return 'Base link';
  return description ? `${variant.toUpperCase()} · ${description}` : `Variant ${variant.toUpperCase()}`;
}

// --- IDs, sources and terms ------------------------------------------------------------

/** Keeps only what the BE accepts as the user types: lowercase a-z, 0-9, underscore. */
export function sanitizeToken(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9_]/g, '');
}

const SLUG = /^[a-z0-9_]*[a-z0-9]_(\d{2})(\d{2})(\d{2})$/;

/** `_MMDDYY` for a YYYY-MM-DD day — the campaign-ID seed. */
export function slugSuffix(day: string): string {
  const [y, m, d] = day.split('-');
  return `_${m}${d}${y!.slice(2)}`;
}

/** Mirrors BE ValidateCampaignSlug; returns an error message or null. */
export function slugError(slug: string): string | null {
  const m = SLUG.exec(slug);
  if (!m) return 'Lowercase letters, digits and underscores, ending in _MMDDYY (e.g. kermes_092026).';
  const [, mm, dd, yy] = m.map(Number) as [number, number, number, number];
  const date = new Date(Date.UTC(2000 + yy, mm - 1, dd));
  if (date.getUTCMonth() !== mm - 1 || date.getUTCDate() !== dd) return `_${m[1]}${m[2]}${m[3]} is not a real date.`;
  return null;
}

/** Mirrors BE ValidateCampaignTargetURL; returns an error message or null. */
export function targetUrlError(raw: string): string | null {
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return 'Enter a full URL, starting with https://';
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return 'Enter a full URL, starting with https://';
  for (const key of u.searchParams.keys()) {
    const k = key.toLowerCase();
    if (k.startsWith('utm_') || k === 'l') {
      return `Remove "${key}" — campaign links add the UTM parameters and language themselves.`;
    }
  }
  return null;
}

export function isAppUrl(raw: string, appDomain: string | undefined): boolean {
  if (!appDomain) return true; // unknown yet — the BE still enforces it
  try {
    return new URL(raw).host.toLowerCase() === new URL(appDomain).host.toLowerCase();
  } catch {
    return false;
  }
}

// --- copy ---------------------------------------------------------------------------

/** A short link with a copy button. Short links are the only links anyone handles. */
export function CopyLink({ url, label }: { url: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <span className="truncate font-mono text-[12.5px] text-ink">{url}</span>
      <button
        type="button"
        aria-label={label ? `Copy ${label}` : 'Copy link'}
        onClick={(e) => {
          e.stopPropagation();
          void navigator.clipboard?.writeText(url).then(() => setCopied(true));
        }}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-app border-0 bg-transparent text-muted hover:bg-[rgba(143,68,32,.08)] hover:text-orange"
      >
        {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
      </button>
    </span>
  );
}

// --- typeahead ---------------------------------------------------------------------------

export interface TypeaheadOption {
  value: string;
  label: ReactNode;
  detail?: ReactNode;
}

/**
 * A single-value combobox. `freeText` keeps whatever is typed (source); without
 * it only a picked option counts (connector profile). The caller owns the
 * search term → options query.
 */
export function Typeahead({
  id,
  value,
  display,
  onTermChange,
  onPick,
  options,
  searching = false,
  placeholder,
  freeText = false,
  sanitize,
  invalid,
}: {
  id?: string;
  /** Shown in the box: the free text, or the picked option's label. */
  value: string;
  display?: string;
  onTermChange: (term: string) => void;
  onPick: (option: TypeaheadOption) => void;
  options: TypeaheadOption[];
  searching?: boolean;
  placeholder?: string;
  freeText?: boolean;
  sanitize?: (v: string) => string;
  invalid?: boolean;
}) {
  const auto = useId();
  const inputId = id ?? auto;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const showList = open && options.length > 0;

  const pick = (o: TypeaheadOption) => {
    onPick(o);
    setOpen(false);
    setActive(0);
  };

  return (
    <div className="relative font-body">
      <input
        ref={inputRef}
        id={inputId}
        role="combobox"
        aria-expanded={showList}
        aria-controls={`${inputId}-list`}
        aria-autocomplete="list"
        aria-invalid={invalid || undefined}
        aria-activedescendant={showList && options[active] ? `${inputId}-opt-${active}` : undefined}
        value={display ?? value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          const v = sanitize ? sanitize(e.target.value) : e.target.value;
          onTermChange(v);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActive((a) => Math.min(a + 1, Math.max(options.length - 1, 0)));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter' && showList && options[active]) {
            e.preventDefault();
            pick(options[active]);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        className={cx(
          'w-full rounded-input border bg-white px-3.5 py-[13px] font-body text-[15px] text-ink outline-none',
          'focus:border-orange focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-orange',
          invalid ? 'border-danger' : 'border-[rgba(143,68,32,.12)]',
        )}
      />
      {open && (showList || (searching && !freeText)) && (
        <ul
          id={`${inputId}-list`}
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 m-0 max-h-[260px] list-none overflow-y-auto rounded-app-card border border-panel-border bg-white p-1 shadow-lg"
        >
          {options.length === 0 ? (
            <li className="px-3 py-2.5 text-[13px] text-muted">Searching…</li>
          ) : (
            options.map((o, i) => (
              <li
                key={o.value}
                id={`${inputId}-opt-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o);
                }}
                onMouseEnter={() => setActive(i)}
                className={cx('cursor-pointer rounded-app px-3 py-2', i === active && 'bg-[rgba(143,68,32,.06)]')}
              >
                <div className="text-sm font-semibold text-ink">{o.label}</div>
                {o.detail && <div className="text-xs text-muted">{o.detail}</div>}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

/** Debounces a value (search terms). */
export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export type { CampaignChannel };
